import useSWR from 'swr';
import { useWeb3React } from '@web3-react/core';
import { verifyChainConfig, subgraphQuery } from '../../helpers';
import { SubgraphMeta } from '../../config/constants';

enum Health {
    /** Subgraph syncing normally */
    Healthy = 'healthy',
    /** Subgraph syncing but with errors */
    Unhealthy = 'unhealthy',
    /** Subgraph halted due to errors */
    Failed = 'failed',
}

type SubgraphIndexingStatus = {
    // Note that the health can be implied from fatalError and nonFatalErrors:
    // - If fatalError is non-null, then health is 'failed'.
    // - Else if nonFatalErrors is non-empty, then health is 'unhealthy'.
    // - Else health is 'healthy'.
    health: Health;
};

const useSubgraphHealth = () => {
    const { chainId } = useWeb3React();

    const { data: metadata } = useSWR(
        chainId ? ['subgraphMetadata', chainId] : null,
        async (_, chainId) => {
            verifyChainConfig(chainId);

            const query = `
                _meta {
                    deployment
                }
            `;

            const data: { deployment: SubgraphMeta } = await subgraphQuery(query, {
                chainId,
            });

            return data.deployment;
        },
        // disable revalidation since we only need subgraphId once
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    const { data, error } = useSWR(
        chainId && metadata ? ['subgraphHealth', chainId, metadata] : null,
        async (_, chainId, subgraphID) => {
            verifyChainConfig(chainId);

            const query = `
                indexingStatuses(subgraphs: ["${subgraphID}"]) {
                    subgraph
                    synced
                    health
                    entityCount
                    fatalError {
                        handler
                        message
                        deterministic
                        block {
                            hash
                            number
                        }
                        }
                        chains {
                        chainHeadBlock {
                            number
                        }
                        earliestBlock {
                            number
                        }
                        latestBlock {
                            number
                        }
                    }
                }
            `;

            const data: { indexingStatuses: SubgraphIndexingStatus[] } =
                await subgraphQuery(query, {
                    url: 'https://api.thegraph.com/index-node/graphql',
                });

            return data.indexingStatuses[0].health === Health.Healthy;
        },
        {
            refreshInterval: 5000,
            dedupingInterval: 5000,
        }
    );

    return {
        isHealthy: data,
        isLoading: !data && !error,
        error,
    };
};

export default useSubgraphHealth;
